<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="isOpen" class="fixed inset-0 z-[10000] flex items-center justify-center p-4 overflow-y-auto modal-container">
        <!-- Backdrop -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 backdrop-blur-none"
          enter-to-class="opacity-100 backdrop-blur-sm"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 backdrop-blur-sm"
          leave-to-class="opacity-0 backdrop-blur-none"
        >
          <div v-if="isOpen" class="absolute inset-0 bg-gray-900/50 dark:bg-gray-950/70 backdrop-blur-sm" @click="$emit('close')"></div>
        </Transition>

        <!-- Close Button -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out delay-200"
          enter-from-class="opacity-0 scale-75"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition-all duration-150 ease-in"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-75"
        >
          <button 
            v-if="isOpen"
            @click="$emit('close')"
            class="absolute top-4 right-4 z-10 p-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Schließen"
          >
            <XMarkIcon class="w-8 h-8" />
          </button>
        </Transition>

        <!-- Modal Content -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 scale-95 translate-y-4"
          enter-to-class="opacity-100 scale-100 translate-y-0"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 scale-100 translate-y-0"
          leave-to-class="opacity-0 scale-95 translate-y-4"
        >
          <div v-if="isOpen" class="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col modal-content">
          <!-- Header -->
          <div class="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <h2 class="text-3xl font-bold text-gray-900 dark:text-white">
              {{ project.title }}
            </h2>
          </div>

          <!-- Content -->
          <div class="overflow-y-auto flex-1 p-6">
            <!-- Screenshots Gallery -->
            <div v-if="project.screenshots && project.screenshots.length > 0" class="mb-6">
              <div class="relative w-full h-64 md:h-96 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-900">
                <Transition
                  mode="out-in"
                  enter-active-class="transition-all duration-300"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition-all duration-200"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <img
                    :key="currentImageIndex"
                    :src="project.screenshots[currentImageIndex]"
                    :alt="`${project.title} - Screenshot ${currentImageIndex + 1}`"
                    class="w-full h-full object-contain"
                    loading="eager"
                    @error="handleImageError"
                  />
                </Transition>

                <!-- Navigation Arrows -->
                <button
                  v-if="project.screenshots.length > 1"
                  @click="previousImage"
                  class="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-all shadow-lg"
                  aria-label="Vorheriges Bild"
                >
                  <ChevronLeftIcon class="w-6 h-6" />
                </button>
                <button
                  v-if="project.screenshots.length > 1"
                  @click="nextImage"
                  class="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 transition-all shadow-lg"
                  aria-label="Nächstes Bild"
                >
                  <ChevronRightIcon class="w-6 h-6" />
                </button>

                <!-- Navigation Dots -->
                <div
                  v-if="project.screenshots.length > 1"
                  class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2"
                >
                  <button
                    v-for="(_, index) in project.screenshots"
                    :key="index"
                    @click="currentImageIndex = index"
                    :class="[
                      'w-2 h-2 rounded-full transition-all duration-300',
                      currentImageIndex === index
                        ? 'bg-white w-6'
                        : 'bg-white/50 hover:bg-white/75'
                    ]"
                    :aria-label="`Gehe zu Screenshot ${index + 1}`"
                  />
                </div>
              </div>
            </div>

            <!-- Logo Display (wenn keine Screenshots vorhanden) -->
            <div v-else-if="hasLogos" class="mb-6">
              <div class="relative w-full h-48 md:h-64 rounded-lg overflow-hidden bg-white dark:bg-gray-900 flex items-center justify-center p-8">
                <img 
                  ref="logoElement"
                  :key="`logo-${project.title}-${resolvedTheme}-${activeLogo}-${logoReloadTrigger}`" 
                  :src="activeLogo" 
                  :alt="project.title" 
                  class="w-auto h-full max-w-[90%] object-contain transition-all duration-300 drop-shadow-2xl"
                  :class="{ 'opacity-0': !logoLoaded && !logoError }"
                  loading="lazy"
                  @load="handleLogoLoad"
                  @error="handleLogoError"
                />
                <!-- Loading Placeholder -->
                <div v-if="!logoLoaded && !logoError && activeLogo" class="absolute inset-0 flex items-center justify-center">
                  <div class="w-12 h-12 border-2 border-blue-600/20 border-t-blue-600 rounded-full animate-spin"></div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="mb-6">
              <p class="text-lg text-gray-900 dark:text-white leading-relaxed whitespace-pre-line">
                {{ project.description }}
              </p>
            </div>

            <!-- Technologies -->
            <div v-if="project.technologies && project.technologies.length > 0" class="mb-6">
              <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                Technologien
              </h3>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="tech in project.technologies"
                  :key="tech"
                  class="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {{ tech }}
                </span>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 flex flex-col gap-4">
            <!-- Obere Zeile: Zur Detailseite, Zur Webseite -->
            <div class="flex items-center gap-4 flex-wrap justify-center sm:justify-start">
              <a
                v-if="project.projectPageUrl"
                :href="project.projectPageUrl"
                class="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                :style="{ backgroundColor: accentColor }"
                @click.stop
              >
                <ChevronRightIcon class="w-5 h-5" />
                <span>Zur Detailseite</span>
              </a>
              <a
                v-if="project.liveUrl"
                :href="project.liveUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 dark:bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-700 dark:hover:bg-blue-600 transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
                :style="project.projectPageUrl ? {} : { backgroundColor: accentColor }"
                @click.stop
              >
                <GlobeAltIcon class="w-5 h-5" />
                <span>Zur Webseite</span>
              </a>
              <a
                v-if="project.githubUrl"
                :href="project.githubUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center space-x-2 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                @click.stop
              >
                <CodeBracketIcon class="w-5 h-5" />
                <span>Code</span>
              </a>
            </div>
            
            <!-- Untere Zeile: Schließen über ganze Breite -->
            <button
              @click.stop="closeModal"
              type="button"
              class="w-full px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white font-bold rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
            >
              Schließen
            </button>
          </div>
        </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, computed, nextTick } from 'vue'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, GlobeAltIcon, CodeBracketIcon } from '@heroicons/vue/24/outline'
import { useTheme } from '../composables/useTheme'
import { logger } from '../utils/logger'

interface Project {
  title: string
  description: string
  screenshots: string[]
  technologies: string[]
  liveUrl?: string
  githubUrl?: string
  projectPageUrl?: string
  logoLight?: string
  logoDark?: string
  accentColor?: string
}

interface Props {
  isOpen: boolean
  project: Project
  accentColor: string
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])

const closeModal = () => {
  emit('close')
}

const { isDark, resolvedTheme } = useTheme()
const currentImageIndex = ref(0)
const logoElement = ref<HTMLImageElement | null>(null)
const logoLoaded = ref(false)
const logoError = ref(false)
const logoReloadTrigger = ref(0)

const hasLogos = computed(() => !!(props.project.logoLight || props.project.logoDark))

const activeLogo = computed(() => {
  // Im Dark Mode: logoDark bevorzugen, sonst logoLight als Fallback
  if (isDark.value) {
    return props.project.logoDark || props.project.logoLight || ''
  } 
  // Im Light Mode: logoLight bevorzugen, sonst logoDark als Fallback
  return props.project.logoLight || props.project.logoDark || ''
})

const nextImage = () => {
  if (props.project.screenshots.length > 0) {
    currentImageIndex.value = (currentImageIndex.value + 1) % props.project.screenshots.length
  }
}

const previousImage = () => {
  if (props.project.screenshots.length > 0) {
    currentImageIndex.value = currentImageIndex.value === 0
      ? props.project.screenshots.length - 1
      : currentImageIndex.value - 1
  }
}

const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = 'https://via.placeholder.com/800x600/1f2937/ffffff?text=Screenshot+Placeholder'
}

const handleLogoLoad = () => {
  logoLoaded.value = true
  logoError.value = false
}

const handleLogoError = (event: Event) => {
  const img = event.target as HTMLImageElement
  const currentSrc = img.src
  logoError.value = true
  
  // Versuche Fallback-Logo
  if (isDark.value && props.project.logoLight && currentSrc === props.project.logoDark) {
    img.src = props.project.logoLight
    logoError.value = false
  } else if (!isDark.value && props.project.logoDark && currentSrc === props.project.logoLight) {
    img.src = props.project.logoDark
    logoError.value = false
  } else {
    logger.warn(`Logo konnte nicht geladen werden: ${currentSrc}`)
  }
}

// Reset image index when modal opens and manage body class
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    currentImageIndex.value = 0
    // Reset logo states
    if (hasLogos.value) {
      logoLoaded.value = false
      logoError.value = false
      logoReloadTrigger.value++
    }
    // Füge modal-open Klasse zum Body hinzu für Custom Cursor und verhindere Scroll
    document.body.classList.add('modal-open')
    // Verhindere Body-Scroll
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.paddingRight = `${scrollbarWidth}px`
    document.body.style.overflow = 'hidden'
  } else {
    // Entferne modal-open Klasse vom Body und erlaube Scroll wieder
    document.body.classList.remove('modal-open')
    document.body.style.paddingRight = ''
    document.body.style.overflow = ''
  }
})

// Watcher für Theme-Änderungen - stelle sicher, dass Logo neu geladen wird
watch(activeLogo, (newLogo, oldLogo) => {
  // Wenn sich das Logo ändert (z.B. durch Theme-Wechsel), setze States zurück
  if (newLogo && newLogo !== oldLogo && props.isOpen) {
    logoLoaded.value = false
    logoError.value = false
    
    // Inkrementiere reloadTrigger, um :key zu ändern und Vue zum Neurendern zu zwingen
    logoReloadTrigger.value++
    
    // Force reload des Bildes durch temporäres Leeren und Wiederherstellen des src
    if (logoElement.value) {
      logoElement.value.src = ''
      // Verwende nextTick um sicherzustellen, dass das src-Attribut aktualisiert wird
      nextTick(() => {
        if (logoElement.value && newLogo) {
          logoElement.value.src = newLogo
        }
      })
    }
  }
}, { immediate: false })

// Zusätzlicher Watcher für resolvedTheme, um Theme-Wechsel direkt zu behandeln
watch(resolvedTheme, (newTheme, oldTheme) => {
  if (newTheme !== oldTheme && hasLogos.value && activeLogo.value && props.isOpen) {
    // Setze States zurück für Logo-Reload
    logoLoaded.value = false
    logoError.value = false
    
    // Inkrementiere reloadTrigger, um :key zu ändern und Vue zum Neurendern zu zwingen
    logoReloadTrigger.value++
    
    // Force reload des Bildes
    if (logoElement.value) {
      const newLogoUrl = activeLogo.value
      logoElement.value.src = ''
      nextTick(() => {
        if (logoElement.value && newLogoUrl) {
          logoElement.value.src = newLogoUrl
        }
      })
    }
  }
}, { immediate: false })

// Keyboard navigation
const handleKeydown = (e: KeyboardEvent) => {
  if (!props.isOpen) return
  
  // Nur Navigation, wenn Screenshots vorhanden sind
  if (props.project.screenshots && props.project.screenshots.length > 0) {
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      nextImage()
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      previousImage()
    }
  }
  if (e.key === 'Escape') {
    e.preventDefault()
    emit('close')
  }
}

// Add keyboard listeners
watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    window.addEventListener('keydown', handleKeydown)
  } else {
    window.removeEventListener('keydown', handleKeydown)
  }
})

// Cleanup beim Unmount
onUnmounted(() => {
  document.body.classList.remove('modal-open')
  window.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(156, 163, 175, 0.5);
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(156, 163, 175, 0.7);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(75, 85, 99, 0.5);
}

.dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(75, 85, 99, 0.7);
}

/* Modal Content Animation */
.modal-content {
  will-change: transform, opacity;
  transform-origin: center center;
}

/* Smooth backdrop blur transition */
.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

/* Cursor-Sichtbarkeit in Modalen optimieren */
.modal-container,
.modal-container * {
  cursor: auto !important;
}

/* Custom-Cursor soll über Modal liegen */
:global(.cursor-wrapper) {
  z-index: 10001 !important;
}

/* Wenn Modal offen ist, Custom-Cursor sichtbar machen */
:global(body.modal-open .cursor-wrapper) {
  z-index: 10001 !important;
}

/* Prevent body scroll when modal is open */
:global(body.modal-open) {
  overflow: hidden;
}

/* Smooth transitions for all interactive elements */
.modal-content * {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>

